import { UserPicture } from "@/components/atoms";
import { AdminRoleUserTag } from "@/components/molecules/Users/AdminUserRoleTag";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { IUserDetailForAdmin } from "@/dto/user/user-details-for-admin.dto"
import { IUserRole } from "@/dto/user/user-role.dto";
import { UserRoleEnum, UserStatusEnum } from "@/enums";
import { faCheckCircle, faEllipsis, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef } from "@tanstack/react-table"
import React from "react";

const statusString = (status: UserStatusEnum) => {
    switch (status) {
        case UserStatusEnum.ENABLED:
            return 'Habilitado';
        case UserStatusEnum.DISABLED:
            return 'Deshabilitado';
        case UserStatusEnum.VERIFICATION:
            return 'En verificación';
        default:
            return 'Desconocido';
    }
}

const statusVariant = (status: UserStatusEnum) => {
    switch (status) {
        case UserStatusEnum.ENABLED:
            return 'secondary';
        case UserStatusEnum.DISABLED:
            return 'outline';
        case UserStatusEnum.VERIFICATION:
            return 'default';
        default:
            return 'outline';
    }
}

type ColumnsProps = {
    onApprove: (e: any, user: IUserDetailForAdmin) => void;
    onDeny: (e: any, user: IUserDetailForAdmin) => void;
    onAddRole: (role: UserRoleEnum, user: IUserDetailForAdmin) => void;
    onRemoveRole: (role: UserRoleEnum, user: IUserDetailForAdmin) => void;
};

export const columns = ({ onApprove, onDeny, onAddRole, onRemoveRole }: ColumnsProps): ColumnDef<IUserDetailForAdmin>[] => [
    {
        accessorKey: "avatarURL",
        header: "",
        cell: ({ row }) => {
            const avatarURL: string | undefined = row.getValue('avatarURL');
            return <div className="flex space-x-2 justify-center">
                <UserPicture firstname={row.original.firstname}
                    lastname={row.original.lastname}
                    username={row.original.email}
                    imageURL={avatarURL}
                    />
            </div>
        },
    },
    {
        accessorKey: "firstname",
        header: "Nombre",
        cell: ({ row }) => {
            return <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{row.getValue('firstname')} {row.original.lastname}</div>
                <div className="text-sm text-gray-500 dark:text-gray-100">{row.original.email}</div>
            </div>
        },
    },
    {
        accessorKey: "roles",
        header: "Roles",
        cell: ({ row }) => {
            const roles = row.getValue('roles') as IUserRole[];
            const user = row.original;
            return <div className="flex w-full flex-wrap gap-1">
              { roles.map((role) => <AdminRoleUserTag key={role.roleName} role={role} onDelete={() => onRemoveRole(role.roleName, user)} /> ) }
            </div>
        },
    },
    {
        accessorKey: "status",
        header: "Estatus",
        cell: ({ row }) => {
            const status = row.getValue('status') as UserStatusEnum;
            return <Badge variant={statusVariant(status) as any}>{statusString(status)}</Badge>
        },
    },
    {
        id: "actions",
        cell: ({ row }) => {
            const user = row.original;

            return (
                <div className="flex space-x-2 justify-end">
                    {
                        user.isPendingVerification ?
                            <>
                                <Button onClick={(e) => onApprove(e, user)} size="sm"><FontAwesomeIcon icon={faCheckCircle} /> Aceptar</Button>
                                <Button onClick={(e) => onDeny(e, user)} variant="destructive" size="sm"><FontAwesomeIcon icon={faTimesCircle} /> Rechazar</Button>
                            </>
                            : <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0 text-black dark:text-white">
                                        <span className="sr-only">Open menu</span>
                                        <FontAwesomeIcon icon={faEllipsis} className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuLabel className="text-muted-foreground text-xs">Acciones</DropdownMenuLabel>
                                    {
                                        user.canDisable ?
                                            <>
                                                <DropdownMenuItem onClick={(e) => onDeny(e, user)}>Deshabilitar</DropdownMenuItem>
                                            </>
                                            : <></>
                                    }

                                    {
                                        user.status === UserStatusEnum.DISABLED ?
                                            <>
                                                <DropdownMenuItem onClick={(e) => onApprove(e, user)}>Habilitar</DropdownMenuItem>
                                            </>
                                            : <></>
                                    }
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-muted-foreground text-xs">Zona de peligro</DropdownMenuLabel>
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger>Añadir rol</DropdownMenuSubTrigger>
                                          <DropdownMenuPortal>
                                            <DropdownMenuSubContent>
                                                {
                                                    [UserRoleEnum.MEMBER, UserRoleEnum.ADMIN].map(
                                                        (role) => {
                                                            if (!user.roles.some(userRole => userRole.roleName == role)) {
                                                                return <DropdownMenuItem key={role} onClick={() => onAddRole(role, user)}>{role}</DropdownMenuItem>
                                                            }
                                                        }
                                                    )
                                                }
                                              </DropdownMenuSubContent>
                                          </DropdownMenuPortal>
                                    </DropdownMenuSub>
                                </DropdownMenuContent>
                            </DropdownMenu>
                    }
                </div>
            );
        },
    },
]
